'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Ad {
  id: string;
  title: string;
  ad_type: 'image' | 'adsense' | 'custom_html' | 'script';
  image_url?: string | null;
  link_url?: string | null;
  adsense_slot?: string | null;
  custom_html?: string | null;
}

type Placement =
  | 'banner_top' | 'banner_bottom' | 'sidebar' | 'popup' | 'between_content' |'popunder'| 'synopsis_between' | 'below_episodes' | 'above_comments' |'after_series'| 'after_latest' | 'between_episodes' | 'between_comments' |'between_genres' | 'between_series_recent' | 'between_details';

interface AdBannerProps {
  placement: Placement;
  className?: string;
}

/**
 * Completely rewrites the container's DOM with real executable script elements. * This bypasses React's virtual DOM entirely so ad scripts actually run.
 */
function mountAdHtml(container: HTMLElement, html: string) {
  // Clear previous content
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  const nodes = Array.from(wrapper.childNodes);

  nodes.forEach((node) => {
    if ((node as Element).tagName === 'SCRIPT') {
      const original = node as HTMLScriptElement;
      const script = document.createElement('script');

      // Copy all attributes
      Array.from(original.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });

      // For external scripts set async
      if (original.src) {
        script.src = original.src;
        script.async = true;
      } else {
        // Inline script — must set text content AFTER appending to trigger execution
        script.text = original.innerHTML;
      }

      container.appendChild(script);
    } else {
      container.appendChild(node.cloneNode(true));
    }
  });
}

export default function AdBanner({ placement, className = '' }: AdBannerProps) {
  const [ad, setAd] = useState<Ad | null>(null);
  const [ready, setReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await supabase
          .from('advertisements')
          .select('id, title, ad_type, image_url, link_url, adsense_slot, custom_html')
          .eq('placement', placement)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (!cancelled && data) {
          setAd(data);
          setReady(true);
        }
      } catch {
        // silent fail
      }
    }
    load();
    return () => { cancelled = true; };
  }, [placement]);

  // Mount ad scripts after the container is in the DOM
  useEffect(() => {
    if (!ready || !ad || !containerRef.current) return;
    if (ad.ad_type === 'script' || ad.ad_type === 'custom_html') {
      if (ad.custom_html) {
        mountAdHtml(containerRef.current, ad.custom_html);
      }
    }
  }, [ready, ad]);

  if (!ad) return null;

  // Image ad
  if (ad.ad_type === 'image' && ad.image_url) {
    return (
      <div className={`w-full flex justify-center py-3 ${className}`}>
        {ad.link_url ? (
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full max-w-4xl rounded-xl overflow-hidden border border-white/10 hover:border-primary/40 transition-all"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={ad.image_url} alt={ad.title} className="w-full h-auto object-cover" />
          </a>
        ) : (
          <div className="w-full max-w-4xl rounded-xl overflow-hidden border border-white/10">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={ad.image_url} alt={ad.title} className="w-full h-auto object-cover" />
          </div>
        )}
      </div>
    );
  }

  // AdSense ad
  if (ad.ad_type === 'adsense' && ad.adsense_slot) {
    return (
      <div className={`w-full flex justify-center py-3 ${className}`}>
        <div className="w-full max-w-4xl overflow-hidden rounded-xl">
          <ins
            className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-client={process.env.NEXT_PUBLIC_ADSENSE_ID}
            data-ad-slot={ad.adsense_slot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      </div>
    );
  }

  // Script / custom HTML ad — rendered via DOM injection in useEffect above
  if ((ad.ad_type === 'script' || ad.ad_type === 'custom_html') && ad.custom_html) {
    return (
      <div className={`w-full flex justify-center py-3 ${className}`}>
        <div ref={containerRef} className="w-full max-w-4xl" />
      </div>
    );
  }

  return null;
}
